import { SearchCustomerInput } from 'gql/__generated/vp-apollo';
import { ControlledInput } from 'components/form/ControlledInput';
import { Button, Container, CopyText, Flex, Grid } from '@dtpk-cc/components';
import { InfoModal } from 'components/content/InfoModal';
import { AlertHelpDefault } from '@dtpk-cc/components/dist/icons';
import { Link } from 'react-router-dom';
import { useToggle } from 'hooks/useToggle';
import { RouterTextLink } from 'components/content/RouterTextLink';
import { useCustomerSearchFormContext } from '../hooks/useCustomerSearchForm';

type CustomerSearchFormProps = {
    submit: (customerData: SearchCustomerInput) => void;
    skipPath: string;
    goBackPath?: string;
};

export const CustomerSearchForm = ({ submit, skipPath, goBackPath }: CustomerSearchFormProps) => {
    const { handleSubmit, lockSubmission } = useCustomerSearchFormContext();
    const [hasInfoModal, toggleInfoModal] = useToggle();

    return (
        <form onSubmit={handleSubmit(submit)} autoComplete="off" noValidate>
            <Container variants={Container.Variant.paddedTop}>
                <Grid>
                    <Grid.Row gutter>
                        <Grid.Col s={6} m={3} l={3} xl={6} marginBottom>
                            <ControlledInput
                                inputMode="numeric"
                                name="phone.areaCode"
                                id="areaCode"
                                data-test-section="phone-number-area-code"
                                autoFocus
                                minLength={3}
                                maxLength={6}
                            >
                                Vorwahl*
                            </ControlledInput>
                        </Grid.Col>
                        <Grid.Col s={6} m={9} l={9} xl={18} marginBottom>
                            <ControlledInput
                                inputMode="numeric"
                                name="phone.cli"
                                id="cli"
                                data-test-section="phone-number-cli"
                            >
                                Rufnummer*
                            </ControlledInput>
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row gutter>
                        <Grid.Col s={6} m={12} l={12} xl={24} marginBottom>
                            <ControlledInput
                                name="customerNumber"
                                id="customerNumber"
                                data-test-section="customer-number"
                                mask={{
                                    mask: '9999999999',
                                    char: '–',
                                }}
                                button={{
                                    icon: AlertHelpDefault,
                                    onClick: toggleInfoModal,
                                    label: 'Kundennummer nicht zur Hand?',
                                }}
                            >
                                Kundennummer*
                            </ControlledInput>
                            <InfoModal
                                headline="Kundennummer nicht zur Hand?"
                                isOpen={hasInfoModal}
                                afterClose={toggleInfoModal}
                            >
                                <CopyText>
                                    Sie finden Ihre Kundennummer auf Ihrer Telefonrechnung und im Kundencenter. <br />
                                    Sollten Sie diese nicht zur Hand haben, können Sie den{' '}
                                    <Link to={skipPath} replace data-test-action="skip-customer-login">
                                        Login überspringen.
                                    </Link>
                                </CopyText>
                            </InfoModal>
                        </Grid.Col>
                    </Grid.Row>
                </Grid>
                <CopyText variants={CopyText.Variant.additional}>
                    * Die gekennzeichneten Felder sind Pflichtangaben.
                </CopyText>
            </Container>
            <Container variants={Container.Variant.paddedTop}>
                <Flex
                    wrap
                    gap
                    alignContent="center"
                    alignItems="center"
                    flexDirection="column"
                    customClass={goBackPath ? 'with-back-link' : 'without-back-link'}
                >
                    <Button
                        type="submit"
                        id="login-customer"
                        data-test-action="login-customer"
                        disabled={lockSubmission}
                    >
                        {lockSubmission ? 'Bitte warten...' : 'Vertrag suchen'}
                    </Button>
                    {!!goBackPath && (
                        <RouterTextLink data-test-action="goto-to-customer-type-selection" to={goBackPath} replace>
                            Zurück zur Vertragsauswahl
                        </RouterTextLink>
                    )}
                </Flex>
            </Container>
        </form>
    );
};
